import axios from 'axios';

import type { ClaimFieldConfigType, ClaimFieldConfigurationMap } from '~/components/ClaimPage/types';
import type {
  ClaimFieldsConfigurationMoveJson,
  ClaimFieldsFormDetailResponse,
  ClaimFieldsFormsResponse,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import type { ClaimBarConfigurationSaveBody, ClaimBarConfigurationSaveResponse } from './types';

const getBaseUrl = (organization_id: number) => `/api/v1/organizations/${organization_id}/claim_fields_configuration`;

export const getFormsUrl = (organization_id: number) => `${getBaseUrl(organization_id)}/forms`;

export const getFormUrl = (organization_id: number, formId: number) => `${getFormsUrl(organization_id)}/${formId}`;

export const getCatalogUrl = (organization_id: number) => `${getBaseUrl(organization_id)}/catalog`;

export const updateField = async <T extends ClaimFieldConfigType>(
  organization_id: number,
  config_type: T,
  field_key: ClaimFieldConfigurationMap[T]['field_key'],
  { isEnabled, displayName }: { isEnabled?: boolean; displayName?: string }
): Promise<void> => {
  try {
    const url = `${getBaseUrl(organization_id)}/${config_type}/${field_key}`;
    await axios.put(url, { is_enabled: isEnabled, display_name: displayName });
  } catch (error) {
    reportAxiosError(error);
  }
};

type MoveOutcome = 'moved' | 'listChanged' | 'failed';

/**
 * Move the field displayed at `fromIndex` to the displayed position `toIndex`. The route indexes the
 * stored column, which holds fields a screen may filter out, so each position is translated through
 * the key of the field displayed there. `listChanged` is the server's 409: the list the screen showed
 * is no longer the stored one, so the caller tells the user and reloads. A displayed field the stored
 * list does not hold yet means the same, so nothing is sent.
 */
export const moveDisplayedField = async <T extends ClaimFieldConfigType>(
  organization_id: number,
  config_type: T,
  storedFields: Pick<ClaimFieldConfigurationMap[T], 'field_key'>[],
  displayedFields: Pick<ClaimFieldConfigurationMap[T], 'field_key'>[],
  fromIndex: number,
  toIndex: number
): Promise<MoveOutcome> => {
  const toStoredIndex = (index: number) =>
    storedFields.findIndex((field) => field.field_key === displayedFields[index].field_key);
  const move: ClaimFieldsConfigurationMoveJson = {
    field_key: displayedFields[fromIndex].field_key,
    from_index: toStoredIndex(fromIndex),
    to_field_key: displayedFields[toIndex].field_key,
    to_index: toStoredIndex(toIndex),
  };
  if (move.from_index === -1 || move.to_index === -1) {
    return 'listChanged';
  }
  try {
    await axios.post(`${getBaseUrl(organization_id)}/${config_type}/move`, move);
    return 'moved';
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return 'listChanged';
    }
    reportAxiosError(error);
    return 'failed';
  }
};

// The configuration writes below rethrow, so the screen reloads only what actually changed.

export const saveClaimBarConfiguration = async (
  organization_id: number,
  body: ClaimBarConfigurationSaveBody
): Promise<ClaimBarConfigurationSaveResponse> => {
  try {
    // A retry after a lost response would send the same additions again, which the server refuses as already
    // stored; the caller reads back instead.
    const { data } = await axios.put<ClaimBarConfigurationSaveResponse>(
      `${getBaseUrl(organization_id)}/claim_bar/configuration`,
      body,
      { 'axios-retry': { retries: 0 } }
    );
    return data;
  } catch (error) {
    if (!isLostResponse(error)) {
      reportAxiosError(error);
    }
    throw error;
  }
};

/** The request reached no answer (a dropped connection or a timeout), so whether the server acted is unknown. */
export const isLostResponse = (error: unknown) => axios.isAxiosError(error) && error.response === undefined;

/** The configuration a scope has stored, or `null` when it has none. Unreported: the caller says what failed. */
export const getStoredConfiguration = async (
  organization_id: number,
  scope: { lob: string | null; subOrganizationId: number | null }
): Promise<ClaimFieldsFormDetailResponse | null> => {
  const { data } = await axios.get<ClaimFieldsFormsResponse>(getFormsUrl(organization_id));
  const form = data.forms.find(
    ({ lob, sub_organization_id }) => lob === scope.lob && sub_organization_id === scope.subOrganizationId
  );
  if (form === undefined) {
    return null;
  }
  const { data: stored } = await axios.get<ClaimFieldsFormDetailResponse>(getFormUrl(organization_id, form.id));
  return stored;
};

export const getForm = async (organization_id: number, formId: number): Promise<ClaimFieldsFormDetailResponse> => {
  try {
    const { data } = await axios.get<ClaimFieldsFormDetailResponse>(getFormUrl(organization_id, formId));
    return data;
  } catch (error) {
    reportAxiosError(error);
    throw error;
  }
};

export const resetForm = async (organization_id: number, formId: number): Promise<void> => {
  try {
    await axios.post(`${getFormUrl(organization_id, formId)}/reset`);
  } catch (error) {
    reportAxiosError(error);
    throw error;
  }
};
